import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppIcon, { LogoBadge } from './icons/AppIcon';
import UiIcon from './icons/UiIcon';
import UserAvatar from './UserAvatar';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../context/ThemeContext';
import {
  canAccessNavItem,
  getAccessibleNavGroups,
  getAccessibleNavItems,
} from '../navigation/appNavigation';
import NotificationBell from './NotificationBell';
import AppFooter from './AppFooter';
// NOTE: Bloom-gift / incoming-call / chat-sound global mounts moved to
// `backup-not-used/` (not used by MedTransact).

const COLLAPSE_KEY = 'app_sidebar_collapsed';
const GROUPS_KEY = 'app_sidebar_open_groups';
const NARROW_QUERY = '(max-width: 1023px)';

/* Reactive narrow-viewport flag — the same 1024px breakpoint the CSS uses for
   the off-canvas sidebar (`@media (max-width: 1023px)`). We need this in JS so
   `effectivelyCollapsed` only honors the persisted desktop `collapsed` state
   on wide viewports. On mobile the sidebar should always show labels + group
   headers, regardless of what the user did on desktop. */
function useIsNarrow() {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(NARROW_QUERY).matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(NARROW_QUERY);
    const handler = (e) => setNarrow(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return narrow;
}

/* System logo. Defaults to CRA's `/logo192.png` so the navbar AND the Bloom
   Stranger sidebar entry (Rule 75, AppIcon `bloomStranger`) share ONE asset:
   replace `public/logo192.png` once and every brand surface — navbar logo,
   sidebar collapsed icon, OS PWA manifest icon — updates from the same file.
   Override per-deployment via REACT_APP_LOGO_URL (any /public path or absolute
   URL). Maximum recommended height is 1.375 rem (22 px) for the macOS toolbar. */
const LOGO_URL = process.env.REACT_APP_LOGO_URL || '/logo192.png';

/* The bundled icon system uses ALL-ORIGINAL inline-SVG components
   (see `./icons/AppIcon.jsx` for polished macOS-style app icons and
   `./icons/UiIcon.jsx` for utility chrome icons) — no third-party glyph
   copied (IP-clean by construction; Rule 56). This `Icon` shim keeps every
   existing `<Icon iconKey="...">` call site working: the `chevron` key
   forwards to `UiIcon` (utility), everything else to `AppIcon` (polished). */
function Icon({ iconKey, className }) {
  if (iconKey === 'chevron') {
    return <UiIcon name="chevronForward" className={className || 'h-3.5 w-3.5'} />;
  }
  return <AppIcon name={iconKey} size={22} className={className} />;
}

function ThemeSwitch() {
  const { t } = useTranslation();
  const { isDark, toggleTheme } = useTheme();
  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={t('shell.toggleTheme')}
      title={isDark ? t('shell.light') : t('shell.dark')}
      className="shell-btn shell-btn-theme"
    >
      {isDark
        ? <AppIcon name="themeSun"  size={22} />
        : <AppIcon name="themeMoon" size={22} />}
    </button>
  );
}

/** Account avatar + dropdown. `variant`: 'top' (opens downward) | 'rail' (opens upward). */
function AccountMenu({ variant }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  const items = [
    { label: t('shell.profile'),         to: '/profile' },
    { label: t('shell.changePassword'),  to: '/profile/change-password' },
    { label: t('shell.sessions'),        to: '/profile/sessions' },
    { label: t('shell.mfa'),             to: '/profile/mfa' },
    { label: t('shell.linkedAccounts'),  to: '/profile/linked-accounts' },
  ];

  return (
    <div className={`acct acct-${variant}`} ref={wrapRef}>
      <button
        type="button"
        className="acct-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <UserAvatar user={user} className="acct-avatar" />
        <span className="acct-id min-w-0">
          <span className="acct-name truncate">{user?.full_name ?? user?.email ?? t('shell.unknownUser')}</span>
          <span className="acct-mail truncate">{user?.email ?? ''}</span>
        </span>
        <Icon iconKey="chevron" className="acct-caret h-3.5 w-3.5" />
      </button>

      {open && (
        <div className="acct-menu" role="menu" aria-label={t('shell.account')}>
          <div className="acct-menu-head">
            <UserAvatar user={user} className="acct-avatar acct-avatar-lg" />
            <div className="min-w-0">
              <p className="acct-name truncate">{user?.full_name ?? t('shell.unknownUser')}</p>
              <p className="acct-mail truncate">{user?.email ?? ''}</p>
            </div>
          </div>
          {items.map((it) => (
            <button
              key={it.to}
              type="button"
              role="menuitem"
              className="acct-menu-item"
              onClick={() => { setOpen(false); navigate(it.to); }}
            >
              {it.label}
            </button>
          ))}
          <div className="acct-menu-divider" />
          <button
            type="button"
            role="menuitem"
            className="acct-menu-item acct-menu-item-danger"
            onClick={() => { setOpen(false); logout(); }}
          >
            {t('shell.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}

function NavItemLink({ item, collapsed, onNavigate }) {
  return (
    <NavLink
      to={item.path}
      title={collapsed ? item.label : undefined}
      onClick={onNavigate}
      className={({ isActive }) => `shell-nav-item ${isActive ? 'is-active' : ''}`}
    >
      <span className="shell-nav-icon"><Icon iconKey={item.iconKey} /></span>
      {!collapsed && (
        <span className="shell-nav-label truncate">{item.label}</span>
      )}
    </NavLink>
  );
}

export default function AppShell() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(false);          // mobile off-canvas
  const [collapsed, setCollapsed] = useState(() => {
    try { return window.localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const isNarrow = useIsNarrow();
  // `collapsed` is the persisted DESKTOP preference. On narrow viewports the
  // off-canvas sidebar should always be in full-label mode regardless — the
  // user's desktop rail-mode choice shouldn't leak into mobile.
  const effectivelyCollapsed = collapsed && !isNarrow;
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem(GROUPS_KEY) || '{}') || {}; } catch { return {}; }
  });

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);
  useEffect(() => {
    try { window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);
  useEffect(() => {
    try { window.localStorage.setItem(GROUPS_KEY, JSON.stringify(openGroups)); } catch { /* ignore */ }
  }, [openGroups]);

  const userScopes = useMemo(
    () => (Array.isArray(user?.scopes) ? user.scopes : []),
    [user?.scopes],
  );
  const navGroups = useMemo(() => getAccessibleNavGroups(userScopes), [userScopes]);
  const flatItems = useMemo(() => getAccessibleNavItems(userScopes), [userScopes]);

  const accountPaths = ['/profile', '/profile/change-password', '/profile/sessions', '/profile/mfa', '/profile/account-security', '/profile/linked-accounts', '/profile/complete'];
  // Dynamic param-bearing routes that live INSIDE the AppShell but are NOT in
  // APP_NAV_ITEMS (they're reached via in-app actions, not the sidebar). The
  // scope gate for these lives at the entry point (e.g. <CallButton> already
  // scope-checks `calls.create`) and at the backend (oauth.scope middleware on
  // every /calls/* route), so the AppShell just needs to recognise the path
  // pattern and render <Outlet />. Add new patterns here when introducing a
  // param-route module — Rule 74 lesson.
  const dynamicAllowedPatterns = [
    /^\/call\/[^/]+$/,            // /call/{callId}            — CallPage
    /^\/threads\/[^/]+\/calls$/,  // /threads/{threadId}/calls — CallHistoryPage
  ];
  const activeItem = useMemo(
    () => flatItems.find((item) => item.path === location.pathname) ?? null,
    [flatItems, location.pathname],
  );
  const moduleTitle = activeItem?.label ?? t('shell.account');
  const moduleTagline = activeItem?.description ?? t('shell.defaultTagline');
  const hasCurrentNavAccess =
    flatItems.some((i) => i.path === location.pathname)
    || accountPaths.includes(location.pathname)
    || dynamicAllowedPatterns.some((re) => re.test(location.pathname));

  // Auto-open the group that contains the active route.
  useEffect(() => {
    const active = navGroups.find(
      (g) => g.type === 'group' && g.children.some((c) => c.path === location.pathname),
    );
    if (active && !openGroups[active.label]) {
      setOpenGroups((prev) => ({ ...prev, [active.label]: true }));
    }
  }, [location.pathname, navGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGroup = (label) =>
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));

  const closeMobile = () => setSidebarOpen(false);

  return (
    <div className={`ubuntu-shell mac-shell ${effectivelyCollapsed ? 'is-collapsed' : ''}`}>
      {/* macOS unified toolbar / title bar */}
      <header className="shell-topbar">

        <button
          type="button"
          className="shell-btn shell-btn-menu shell-btn-menu-mobile"
          onClick={() => setSidebarOpen((v) => !v)}
          aria-label={t('shell.openMenu')}
        >
          <span /><span /><span />
        </button>

        <button
          type="button"
          className="shell-btn shell-btn-menu shell-btn-menu-desktop"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={t('shell.toggleSidebar')}
          aria-pressed={collapsed}
        >
          <span /><span /><span />
        </button>

        <a href="/" className="shell-logo" aria-label={t('app.name')}>
          {/* Logo wrapped in the polished squircle (LogoBadge) so it reads as
              part of the macOS icon family instead of a bare PNG (Rule 56). */}
          {LOGO_URL
            ? <LogoBadge src={LOGO_URL} alt={t('app.name')} size={30} className="shell-logo-badge" />
            : <span className="shell-logo-word">{t('app.name')}</span>}
        </a>

        <div className="shell-headline min-w-0">
          <h1 className="shell-title truncate">{moduleTitle}</h1>
          <p className="shell-tagline truncate">{moduleTagline}</p>
        </div>

        <div className="shell-tools">
          <ThemeSwitch />
          {canAccessNavItem(userScopes, { requiredScopes: ['notifications.view'], path: '/notifications' }) && (
            <NotificationBell />
          )}
          <AccountMenu variant="top" />
        </div>
      </header>

      <div className="shell-body">
        {sidebarOpen && (
          <button
            type="button"
            className="shell-overlay lg:hidden"
            aria-label={t('common.dismiss')}
            onClick={closeMobile}
          />
        )}

        {/* macOS source-list sidebar */}
        <aside className={`shell-sidebar ${sidebarOpen ? 'is-open' : ''}`}>
          <nav className="shell-nav-scroll" aria-label={t('shell.primaryNav')}>
            {navGroups.map((entry) => {
              if (entry.type === 'item') {
                return (
                  <NavItemLink
                    key={entry.item.path}
                    item={entry.item}
                    collapsed={effectivelyCollapsed}
                    onNavigate={closeMobile}
                  />
                );
              }
              const isOpen = effectivelyCollapsed ? true : (openGroups[entry.label] ?? true);
              return (
                <div key={entry.label} className="shell-group">
                  {!effectivelyCollapsed && (
                    <button
                      type="button"
                      className="shell-group-head"
                      aria-expanded={isOpen}
                      onClick={() => toggleGroup(entry.label)}
                    >
                      <Icon
                        iconKey="chevron"
                        className={`shell-group-caret h-3 w-3 ${isOpen ? 'is-open' : ''}`}
                      />
                      <span className="shell-group-label truncate">{entry.label}</span>
                    </button>
                  )}
                  {effectivelyCollapsed && (
                    <div className="shell-group-railhead" title={entry.label}>
                      <span className="shell-nav-icon"><Icon iconKey={entry.iconKey} /></span>
                    </div>
                  )}
                  {isOpen && (
                    <div className={`shell-group-children ${effectivelyCollapsed ? 'is-rail' : ''}`}>
                      {entry.children.map((child) => (
                        <NavItemLink
                          key={child.path}
                          item={child}
                          collapsed={effectivelyCollapsed}
                          onNavigate={closeMobile}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="shell-sidebar-foot">
            <AccountMenu variant="rail" />
          </div>
        </aside>

        <main className="shell-main">
          <div className="shell-content">
            {!hasCurrentNavAccess ? (
              <div className="shell-fallback">
                <p className="shell-fallback-title">{t('shell.notAccessible')}</p>
                <p className="shell-fallback-desc">{t('shell.notAccessibleDesc')}</p>
              </div>
            ) : (
              <Outlet />
            )}
          </div>
          <AppFooter />
        </main>
      </div>

      {/* Bloom-gift / incoming-call / chat-sound global mounts removed for
          MedTransact (those modules live in `backup-not-used/`). */}
    </div>
  );
}
